import { Value } from "@stringtale/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { addQueryParams } from "app/core/utils/addQueryParams"
import { usePlay } from "app/radio/components/PlayerContext"
import { playingStateAtom } from "app/radio/state"
import { trpc } from "app/trpc"
import type { Group } from "db/client"
import { useSetAtom } from "jotai"
import Link from "next/link"
import { parseAsString, useQueryState } from "nuqs"
import { DropdownMenu } from "radix-ui"
import { ReactNode } from "react"
import { PX14 } from "theme/sizes"
import ContextMenu, { Separator } from "ui/ContextMenu"

export default function SwitchGroupMenu({
  trigger,
  groups,
  currentGroup,
  onCompleted,
}: {
  trigger: ReactNode
  groups: Group[]
  currentGroup: Group | null
  onCompleted?: () => void
}) {
  const organization = useCurrentOrganization()
  const setGroupForSessionMutation = useMutation(
    trpc.auth.setGroupForSession.mutationOptions()
  )

  const setPlayingState = useSetAtom(playingStateAtom)
  const { pause } = usePlay()
  const queryClient = useQueryClient()
  const setGroupForSessionClickHandler = async (groupId: number) => {
    await setGroupForSessionMutation.mutateAsync({ groupId })
    await queryClient.resetQueries()
    // await queryClient.invalidateQueries()
    await onCompleted?.()
  }

  const [next] = useQueryState("next", parseAsString)

  return (
    <ContextMenu
      trigger={trigger}
      items={
        <>
          <ContextMenu.Label>{organization?.displayTitle}</ContextMenu.Label>
          <Separator />
          {groups.length > 0 &&
            groups.map((group, index) => {
              return (
                <ContextMenu.Item
                  onClick={async () => {
                    await setGroupForSessionClickHandler(group.id)
                    pause()
                    setPlayingState({ type: "NONE" })
                  }}
                >
                  <span
                    style={{
                      fontWeight: `${
                        group.id === currentGroup?.id ? "bold" : "auto"
                      }`,
                    }}
                  >
                    {group.displayTitle}
                  </span>
                </ContextMenu.Item>
              )
            })}
          {groups.length === 0 && (
            <span style={{ padding: "12px 24px", fontSize: PX14 }}>
              <Value name="apps.web.src.groups.components.switchgroupmenu.er_zijn_geen_groepen_aan_je_gekoppeld">
                Je hebt geen groep(en). Klopt dit niet, neem contact op met je
                beheerder.
              </Value>
            </span>
          )}
          {groups.length > 1 && (
            <>
              <Separator />
              <ContextMenu.Item asChild>
                <Link
                  href={addQueryParams(
                    "/groepskeuze",
                    next
                      ? {
                          next,
                        }
                      : {}
                  )}
                >
                  <Value name="apps.web.src.groups.components.switchgroupmenu.groepskeuzemenu">
                    Groepskeuzemenu
                  </Value>
                </Link>
              </ContextMenu.Item>
            </>
          )}

          <DropdownMenu.Arrow
            style={{ fill: "white" }}
            width={30}
            height={15}
          />
        </>
      }
    ></ContextMenu>
  )
}
